/**
 * AnimationBuilder - Converts simple scenario descriptions into full animation sequences
 *
 * This file contains all the complex logic for building animations from simple user-friendly descriptions.
 * The AnimationScenarios.ts file should only contain simple, clear scenario definitions.
 */

import { AnimationSequence } from './PieceRulesAnimator';
import { MoveType } from '../../services/zodiacEngine/types';

// Extended animation sequence with moveDescription and type
export interface EnhancedAnimationSequence extends AnimationSequence {
  moveDescription?: string;
  scenarioType?: 'movement' | 'capture' | 'ability';
}

export interface SimpleScenario {
  name: string;
  description: string;
  fen: string;
  selectAnimation: [number, number];  // [row, col] - Circle animation when piece is first selected
  selectPiece: [number, number];     // [row, col] - Square to select piece in game engine
  travelIndicator: [number, number]; // [row, col] - Square animation showing movement intention
  travelAnimation: [number, number]; // [row, col] - Circle animation signifying AI clicking square
  pieceMovement: [number, number];   // [row, col] - Engine completing move using game engine
  travelIndicator2?: [number, number]; // [row, col] - Second movement intention (optional)
  travelAnimation2?: [number, number]; // [row, col] - Second movement click (optional)
  pieceMovement2?: [number, number];   // [row, col] - Second movement execution (optional)
  postMoveDescription: string;
  type: 'movement' | 'capture' | 'ability';
}

/**
 * Build a complete animation sequence from a simple scenario description
 */
export function buildAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  const [selectAnimationRow, selectAnimationCol] = scenario.selectAnimation;
  const [selectPieceRow, selectPieceCol] = scenario.selectPiece;
  // Use travel coordinates as provided (no auto-adjustment)
  const [travelIndicatorRow, travelIndicatorCol] = scenario.travelIndicator;
  const [travelAnimationRow, travelAnimationCol] = scenario.travelAnimation;
  const [pieceMovementRow, pieceMovementCol] = scenario.pieceMovement;

  return {
    name: scenario.name,
    description: scenario.description,
    moveDescription: scenario.postMoveDescription,
    scenarioType: scenario.type,
    steps: [
      // PERFECT RESET: Clear board completely to prevent disappearing pieces
      {
        type: 'placement' as const,
        data: {
          fen: '8/8/8/8/8',
          soundEffect: 'none' as const
        }
      },

      // Brief delay to ensure clean state
      {
        type: 'wait' as const,
        data: { duration: 100 }
      },

      // 1. Place pieces on board
      {
        type: 'placement' as const,
        data: {
          fen: scenario.fen,
          soundEffect: 'placement' as const
        }
      },

      // 2. Wait for placement to settle
      {
        type: 'wait' as const,
        data: { duration: 100 }
      },

      // 3. Click to select piece (selectAnimation coordinates)
      {
        type: 'click' as const,
        data: {
          fromPosition: { row: selectAnimationRow, col: selectAnimationCol }
        }
      },

      // 4. Wait after selection
      {
        type: 'wait' as const,
        data: { duration: 100 }
      },

      // 5. Rollover destination square (travelIndicator coordinates)
      {
        type: 'rollover' as const,
        data: {
          toPosition: { row: travelIndicatorRow, col: travelIndicatorCol },
          duration: 100
        }
      },

      // 6. Wait before move
      {
        type: 'wait' as const,
        data: { duration: 100 }
      },

      // 7. Click destination (travelAnimation coordinates) - NO PIECE SELECTION
      {
        type: 'click' as const,
        data: {
          fromPosition: { row: travelAnimationRow, col: travelAnimationCol },
          suppressPieceSelection: true  // Prevent selecting enemy pieces during travel animation
        }
      },

      // 8. Execute movement (selectPiece -> pieceMovement coordinates)
      {
        type: 'move' as const,
        data: {
          fromPosition: { row: selectPieceRow, col: selectPieceCol },
          toPosition: { row: pieceMovementRow, col: pieceMovementCol },
          soundEffect: 'movement' as const,
          duration: 10
        }
      },

      // 9-11. Second movement sequence (if exists)
      ...(scenario.travelIndicator2 && scenario.travelAnimation2 && scenario.pieceMovement2 ? [
        // Wait before second movement
        {
          type: 'wait' as const,
          data: { duration: 200 }
        },
        // Second rollover
        {
          type: 'rollover' as const,
          data: {
            toPosition: { row: scenario.travelIndicator2[0], col: scenario.travelIndicator2[1] },
            duration: 100
          }
        },
        // Wait before second click
        {
          type: 'wait' as const,
          data: { duration: 100 }
        },
        // Second click
        {
          type: 'click' as const,
          data: {
            fromPosition: { row: scenario.travelAnimation2[0], col: scenario.travelAnimation2[1] }
          }
        },
        // Second move execution
        {
          type: 'move' as const,
          data: {
            fromPosition: { row: pieceMovementRow, col: pieceMovementCol },
            toPosition: { row: scenario.pieceMovement2[0], col: scenario.pieceMovement2[1] },
            soundEffect: 'movement' as const,
            duration: 10
          }
        }
      ] : []),

      // 12. Deselect and finish
      {
        type: 'deselect' as const,
        data: { duration: 10 }
      },

      // 13. Wait before clearing board
      {
        type: 'wait' as const,
        data: { duration: 2000 }
      },

      // 14. Clear the board
      {
        type: 'placement' as const,
        data: {
          fen: '8/8/8/8/8',
          soundEffect: 'none' as const
        }
      }
    ]
  };
}

/**
 * Build capture animation (similar to movement but with capture effects)
 */
export function buildCaptureAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  const baseAnimation = buildAnimationFromScenario(scenario);

  // Add capture highlight step before the move
  const captureStep = {
    type: 'capture' as const,
    data: {
      capturePosition: { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] },
      soundEffect: 'capture' as const,
      duration: 10
    }
  };

  // Insert capture step before the final move
  baseAnimation.steps.splice(-2, 0, captureStep);

  return baseAnimation;
}

/**
 * Build ability animation (for special piece abilities)
 */
export function buildAbilityAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  // For now, same as movement - can be customized per ability type
  return buildAnimationFromScenario(scenario);
}

/**
 * Build special entangle animation that shows purple highlights
 */
export function buildEntangleAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  const baseAnimation = buildAnimationFromScenario(scenario);

  // Find the move step (should be near the end of the animation)
  const moveStepIndex = baseAnimation.steps.findIndex(step => step.type === 'move');

  if (moveStepIndex !== -1) {
    // Insert deselect step after the move to clear selection
    const deselectStep = {
      type: 'deselect' as const,
      data: {
        duration: 300
      }
    };

    // Insert side effect step after deselect
    const sideEffectStep = {
      type: 'sideeffect' as const,
      data: {
        sideEffectPosition: { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] },
        sideEffectType: 'entangle',
        duration: 3000 // Show entangle effect for 3 seconds (longer to be visible at end)
      }
    };

    baseAnimation.steps.splice(moveStepIndex + 1, 0, deselectStep, sideEffectStep);
  }

  return baseAnimation;
}

/**
 * Build special ink animation for octopus ink moves
 */
export function buildInkAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  // Use the regular animation but specify that this should use ink move
  const baseAnimation = buildAnimationFromScenario(scenario);

  // Find the move step and add moveType specification
  const moveStepIndex = baseAnimation.steps.findIndex(step => step.type === 'move');
  if (moveStepIndex !== -1) {
    const moveStep = baseAnimation.steps[moveStepIndex];
    if (moveStep.data) {
      moveStep.data.moveType = MoveType.ink; // Force ink move type
    }
  }

  // For ink scenario: add monkey selection and modify second move sequence
  if (scenario.travelIndicator2 && scenario.travelAnimation2 && scenario.pieceMovement2) {
    // Find where the second movement sequence starts (after the wait step)
    const waitStepIndex = baseAnimation.steps.findIndex((step, index) =>
      step.type === 'wait' && index > 10 && step.data?.duration === 200
    );

    if (waitStepIndex !== -1) {
      // Insert monkey selection step after the wait
      const monkeySelectionStep = {
        type: 'click' as const,
        data: {
          fromPosition: { row: 4, col: 1 } // Select monkey at [4,1]
        }
      };

      // Insert wait after monkey selection
      const selectionWaitStep = {
        type: 'wait' as const,
        data: { duration: 300 }
      };

      baseAnimation.steps.splice(waitStepIndex + 1, 0, monkeySelectionStep, selectionWaitStep);
    }

    // Find the second move step and modify its fromPosition
    const moves = baseAnimation.steps.filter(step => step.type === 'move');
    if (moves.length > 1) {
      const secondMoveStep = moves[1];
      if (secondMoveStep.data) {
        // Change from position to monkey's original position [4,1]
        secondMoveStep.data.fromPosition = { row: 4, col: 1 };
      }
    }
  }

  return baseAnimation;
}

/**
 * Build special penguin movement animation that includes rotation after movement
 */
export function buildPenguinMovementAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  // Use the regular animation but modify second move to be a rotation
  const baseAnimation = buildAnimationFromScenario(scenario);

  // For penguin movement: modify second move to be a rotation (noMove)
  if (scenario.travelIndicator2 && scenario.travelAnimation2 && scenario.pieceMovement2) {
    // Find the second move step
    const moves = baseAnimation.steps.filter(step => step.type === 'move');
    if (moves.length > 1) {
      const secondMoveStep = moves[1];
      if (secondMoveStep.data) {
        // Change second move to be rotation (fromPosition = toPosition for noMove rotation)
        secondMoveStep.data.fromPosition = { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] };
        secondMoveStep.data.toPosition = { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] };
      }
    }
  }

  return baseAnimation;
}

/**
 * Build special penguin rotation animation that uses noMove rotation
 */
export function buildPenguinRotationAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  // Use the regular animation but remove the move step and time the direction change with deselect
  const baseAnimation = buildAnimationFromScenario(scenario);

  // Find and remove the move step completely
  const moveStepIndex = baseAnimation.steps.findIndex(step => step.type === 'move');
  if (moveStepIndex !== -1) {
    // Remove the move step
    baseAnimation.steps.splice(moveStepIndex, 1);
  }

  // Find the deselect step and place the direction change right before it
  const deselectStepIndex = baseAnimation.steps.findIndex(step => step.type === 'deselect');
  if (deselectStepIndex !== -1) {
    // Add a placement step right before deselect (when highlights disappear)
    const placementStep = {
      type: 'placement' as const,
      data: {
        fen: '8/8/P→7/8/8', // Show penguin facing right after rotation
        soundEffect: 'none' as const
      }
    };

    baseAnimation.steps.splice(deselectStepIndex, 0, placementStep);
  }

  return baseAnimation;
}

/**
 * Build special penguin capture + rotation animation
 */
export function buildPenguinCaptureRotationAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  // Start with capture animation for the first move
  const baseAnimation = buildCaptureAnimationFromScenario(scenario);

  // For the second move, modify it to be a rotation (noMove)
  if (scenario.travelIndicator2 && scenario.travelAnimation2 && scenario.pieceMovement2) {
    // Find the second move step
    const moves = baseAnimation.steps.filter(step => step.type === 'move');
    if (moves.length > 1) {
      const secondMoveStep = moves[1];
      if (secondMoveStep.data) {
        // Change second move to be rotation (fromPosition = toPosition for noMove rotation)
        secondMoveStep.data.fromPosition = { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] };
        secondMoveStep.data.toPosition = { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] };
        secondMoveStep.data.moveType = MoveType.noMove;
      }
    }
    // Let the engine handle the natural rotation based on travel animation coordinates
  }

  return baseAnimation;
}

/**
 * Build special zombification animation that removes zombification after first move
 */
export function buildZombificationAnimationFromScenario(scenario: SimpleScenario): EnhancedAnimationSequence {
  const baseAnimation = buildAnimationFromScenario(scenario);

  // Find the wait step before the second movement (after step 11 in the dual-movement sequence)
  const waitStepIndex = baseAnimation.steps.findIndex((step, index) =>
    step.type === 'wait' && index > 10 && step.data?.duration === 200
  );

  if (waitStepIndex !== -1 && scenario.travelIndicator2 && scenario.travelAnimation2 && scenario.pieceMovement2) {
    // Insert transform step after the wait, before the second rollover
    const transformStep = {
      type: 'transform' as const,
      data: {
        transformPosition: { row: scenario.pieceMovement[0], col: scenario.pieceMovement[1] },
        newPieceType: 'crane',
        newPieceIsLight: false, // Assuming the crane is dark based on FEN
        duration: 1000
      }
    };

    baseAnimation.steps.splice(waitStepIndex + 1, 0, transformStep);
  }

  return baseAnimation;
}